import React from 'react';
import { TextV2, Box, ButtonV2, DialogV2, CancelButton } from '@withjoy/joykit';
import { StyledFlexFooter } from './CurrencyDialog.styles';
import { CurrencySelectorV2 } from '@shared/components/CurrencySelectorV2';
import { useTranslation } from '@shared/core';
import { v1 as uuid } from 'uuid';
import { Divider } from '@apps/registry/common/components';
import { useCurrencyDialogController } from './CurrencyDialog.controller';
import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { useCurrencyLockMessage } from '@shared/hooks/useCurrencyLockMessage';

const OVERLAY_KEY = `currency-picker-${uuid()}`;
const LOCKED_MESSAGE_ID = 'currency-dialog-locked-message';

export type CurrencyDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  eventId: Maybe<string>;
  registryCurrency: Maybe<RegistryCurrencyCodesFragment>;
  setRegistryCurrency?: (c: RegistryCurrencyCodesFragment) => void;
}>;

export const CurrencyDialog: React.FC<CurrencyDialogProps> = ({ isOpen, onClose, eventId, registryCurrency, setRegistryCurrency }) => {
  const { onSave, onCancel, currencies, selectedCurrencyCode, setSelectedCurrencyCode, isCurrencyLocked, lockedCurrencyCode } = useCurrencyDialogController({
    isOpen,
    onClose,
    eventId,
    registryCurrency,
    setRegistryCurrency,
  });
  const { tq } = useTranslation('yourRegistry');
  const lockMessage = useCurrencyLockMessage(lockedCurrencyCode);

  return (
    <DialogV2
      id={OVERLAY_KEY}
      onClose={onClose}
      isOpen={isOpen}
      size="lg"
    >
      <DialogV2.Body>
        <TextV2
          textAlign="left"
          letterSpacing="-1%"
          fontWeight={600}
          fontStyle="normal"
          fontSize={17}
          component="h1"
          marginBottom={2}
        >
          {tq`Registry Currency`}
        </TextV2>
        <TextV2
          component="p"
          marginBottom={6}
          typographyVariant="body1"
        >
          {tq`Select the currency displayed on your registry.`}
        </TextV2>
        <TextV2
          component="p"
          marginBottom={6}
          typographyVariant="body1"
        >
          {tq`We will use this currency symbol for any gift that isn't added from the Joy Shop or synced from an external registry.`}
        </TextV2>
        <TextV2
          component="p"
          marginBottom={54}
          typographyVariant="body1"
        >
          {tq`If you have an existing cash fund with contributions, the currency for that gift will not be changed.`}
        </TextV2>
        <Box marginBottom={7}>
          <CurrencySelectorV2
            value={selectedCurrencyCode}
            onChange={setSelectedCurrencyCode}
            currencies={currencies}
            disabled={isCurrencyLocked}
            searchable
            {...(isCurrencyLocked && { 'aria-describedby': LOCKED_MESSAGE_ID })}
          />
        </Box>
        {lockMessage && (
          <TextV2
            id={LOCKED_MESSAGE_ID}
            component="p"
            typographyVariant="body1"
            color="mono10"
          >
            {lockMessage}
          </TextV2>
        )}
      </DialogV2.Body>
      <Divider margin="0px" />
      <DialogV2.Footer>
        <StyledFlexFooter
          justifyContent="flex-end"
          flexDirection={['column-reverse', 'row']}
          width="100%"
        >
          <CancelButton
            onClick={onCancel}
            width={['100%', '132px']}
          >
            {tq`Cancel`}
          </CancelButton>
          {!isCurrencyLocked && (
            <ButtonV2
              intent="primary"
              onClick={onSave}
              width={['100%', '132px']}
            >
              {tq`Save`}
            </ButtonV2>
          )}
        </StyledFlexFooter>
      </DialogV2.Footer>
    </DialogV2>
  );
};
